import React, { Fragment, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import ProductionValueReportTable from './production-value-report-table/production-value-report-table.component';
import { Accordion, AccordionDetails, AccordionSummary, Box, LinearProgress, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@apollo/client';
import { getProductionValueReportsTemplate } from 'apollo/templates/order';
import { User } from 'models/user';
import { prodAction, ProductionValueReport as ProductionValueReportType, ProductionStep, Order } from 'models/order';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { departmentsTemplate } from 'apollo/templates/organization';
import { Department, Step } from 'models/organization';
import { useValueTemplate } from 'hooks/useValueTemplate';
import { useProductionValueSteps } from 'hooks/useProductionValueSteps';
import { restorationActionRows } from 'utils/restoration-price-rows';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

export type ProductionValueReportRef = {
    refetchProductionValueReports: () => void;
};

type ProductionValueReportProps = {
    order: Order;
    isProductionFinished: boolean;
    fromInternalPage?: boolean;
    isTechnicianAdmin: boolean;
    onDataChange?: (data: { orderId: string; productionValueReports: ProductionValueReportType[] }) => void;
    onValidationChange?: (isValid: boolean) => void;
};

/**
 * DD-3545: a technician filled in from the workflow need not belong to the step's department,
 * so offer them alongside the department's technicians.
 */
const withSelectedTechnician = (step: ProductionStep, departmentTechnicians: User[] = []) => {
    const selected = step.technicians?.find((technician) => technician._id === step.technician);
    const isListed = departmentTechnicians.some((technician) => technician._id === step.technician);
    return selected && !isListed ? [...departmentTechnicians, selected] : departmentTechnicians;
};

const ProductionValueReport = forwardRef<ProductionValueReportRef, ProductionValueReportProps>(
    ({ order, isProductionFinished, fromInternalPage, isTechnicianAdmin, onDataChange, onValidationChange }, ref) => {
        const { t: translate } = useTranslation(['orders', 'lab-settings']);
        const [productionData, setProductionData] = useState<ProductionValueReportType[]>([]);
        const [validationStates, setValidationStates] = useState<Record<number, boolean>>({});
        const [departmentTechnicianMap, setDepartmentTechnicianMap] = useState<Record<string, any[]>>({});
        const { showBoundary } = useDenthubErrorBoundary();
        const { valueTemplates } = useValueTemplate();
        const { productionValueSteps } = useProductionValueSteps();
        const currentUser = currentUserVar();

        const actions = useMemo(() => {
            return restorationActionRows(order?.restorations)
                .filter((row) => !!row.actionId)
                .map((row) => ({
                    name: row.name,
                    number: row.code || '',
                    actionId: row.actionId as string,
                }));
        }, [order]);

        const { data: departmentsData } = useQuery(departmentsTemplate, {
            fetchPolicy: 'network-only',
            // DD-3589: the backend refuses departments to external users below the elevated tier.
            skip: !permissions.canReadDepartments(currentUser.role),
            onCompleted() {
                const departmentsMap: Record<string, any[]> = {};

                departmentsData?.departments?.forEach((department: Department) => {
                    departmentsMap[department.name] = department.technicians ?? [];
                });

                setDepartmentTechnicianMap(departmentsMap);
            },
            onError(error) {
                showBoundary(error);
            },
        });

        const {
            data: { productionValueReports } = {},
            loading,
            refetch,
        } = useQuery(getProductionValueReportsTemplate, {
            fetchPolicy: 'network-only',
            variables: {
                orderId: order?._id,
                actions,
            },
            skip: !order?._id || actions.length === 0,
            onError(error) {
                showBoundary(error);
            },
        });

        //expose refetch to parent
        useImperativeHandle(ref, () => ({
            refetchProductionValueReports() {
                refetch();
            },
        }));

        const handleStepsChange = (actions: prodAction[], steps: ProductionStep[]) => {
            const actionIds = new Set(actions.map((a) => a.actionId));
            const updatedData = productionData.map((prodData) => {
                const matches = prodData.actions.every((a) => actionIds.has(a.actionId));
                return matches ? { ...prodData, steps } : prodData;
            });
            setProductionData(updatedData);
            onDataChange?.({
                orderId: order?._id,
                productionValueReports: updatedData,
            });
        };

        useEffect(() => {
            if (!productionValueReports) return;

            const initialData = productionValueReports.map((productionValue: ProductionValueReportType) => ({
                actions: productionValue.actions,
                templateName: productionValue.templateName,
                steps: productionValue.steps.map((step) => ({
                    step: step.step,
                    department: step.department,
                    technician: step.technician || '',
                    valuePercent: step.valuePercent || 0,
                    technicians: withSelectedTechnician(step, departmentTechnicianMap[step.department]),
                })),
            }));

            setProductionData(initialData);
            onDataChange?.({ orderId: order?._id, productionValueReports: initialData });
        }, [productionValueReports, departmentTechnicianMap]);

        useEffect(() => {
            if (Object.keys(validationStates).length !== productionData.length) return;
            const allValid = Object.values(validationStates).every((isValid) => isValid);
            onValidationChange?.(allValid);
        }, [validationStates, productionData.length]);

        const workflowStepOptions = useMemo(() => {
            if (!departmentsData?.departments) return [];

            return departmentsData.departments.flatMap((department: Department) =>
                (department.workflowSteps ?? []).map((step: Step) => ({
                    _id: step._id,
                    name: step.name,
                    department: department.name,
                    technicians: department.technicians ?? [],
                })),
            );
        }, [departmentsData?.departments]);

        return (
            <Fragment>
                {loading && <LinearProgress color="secondary" />}

                {productionData?.map((productionValue: ProductionValueReportType, index: number) => (
                    <Accordion key={index} variant="outlined" defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box>
                                <Typography variant="h5">
                                    {productionValue.actions
                                        .map((action: prodAction) => `${action.number} ${action.name}`)
                                        .join(', ')}
                                </Typography>
                                <Typography variant="caption" color="grey.500">
                                    {productionValue.templateName
                                        ? `${translate('orders:TEMPLATE')}: ${productionValue.templateName}`
                                        : ''}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductionValueReportTable
                                workflowStepOptions={workflowStepOptions}
                                steps={productionValue.steps}
                                isProductionFinished={isProductionFinished}
                                fromInternalPage={fromInternalPage}
                                isTechnicianAdmin={isTechnicianAdmin}
                                valueTemplates={valueTemplates}
                                productionValueSteps={productionValueSteps}
                                departments={departmentsData?.departments ?? []}
                                actionDeductionValue={productionValue.actions.reduce(
                                    (sum, action) => sum + (action.productionDeductionValue || 0),
                                    0,
                                )}
                                onStepsChange={(steps) => handleStepsChange(productionValue.actions, steps)}
                                onValidationChange={(isValid) =>
                                    setValidationStates((prev) => ({ ...prev, [index]: isValid }))
                                }
                            />
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Fragment>
        );
    },
);
ProductionValueReport.displayName = 'ProductionValueReport';
export default ProductionValueReport;
